import { FC, useEffect, useState } from "react";
import { getPlayerStatsById } from "../../../api/transport/statsTransport";
import { TeamContext } from "../../../types/common";
import { PlayerDto, PlayerPositionGroup, PlayersStats, StatParams } from "../../../types/player";
import { Content, InfoContainer, InfoContent, StatsContainer } from "./styled";
import { StatParameter } from "./StatParameter";
import { InfoParameter } from "./InfoParameter";
import { PlayerMatchesWidget } from "../PlayerMatches";
import { getFormattedDate } from "../../../utils";
import { PlayerTournamentSelectorWidget } from "../PlayerTournamentSelector";
import { TournamentUuids } from "../../../types/stats";

interface PlayerExtendedInfoWidgetProps {
    teamContext?: TeamContext;
    teamId?: number;
    playerId: string;
    playerData?: PlayerDto;
}

export const PlayerExtendedInfoWidget: FC<PlayerExtendedInfoWidgetProps> = ({
    playerId,
    playerData,
    teamContext,
    teamId,
}) => {
    const [playerStats, setPlayerStats] = useState<PlayersStats>();
    const [tournamentUuids, setTournamentUuids] = useState<TournamentUuids>();

    useEffect(() => {
        if (!tournamentUuids?.tournamentUuid || !tournamentUuids?.seasonUuid) {
            return;
        }

        getPlayerStatsById(playerId, tournamentUuids.tournamentUuid, tournamentUuids.seasonUuid, teamContext).then((data) => {
            setPlayerStats(data);
        });
    }, [playerId, tournamentUuids, teamContext]);

    if (!playerData) {
        return null;
    }

    let debutMatch = null;

    if (playerData.debutMatch) {
        const val = `${playerData.debutMatch.homeTeam.name} ${playerData.debutMatch.score} ${playerData.debutMatch.guestTeam.name}`;
        const date = new Date(playerData.debutMatch.date).toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        debutMatch = (
            <InfoParameter
                name={`Дебют за "РОДИНУ" ${date}`}
                value={val}
                link={playerData.debutMatch.matchUrl}
            />
        )
    }

    const isGk = playerData.positionGroup === PlayerPositionGroup.Goalkeeper;

    let filters: {
        params: StatParams[];
        default: StatParams
    } = null;

    if (playerStats) {
        if (isGk) {
            filters = {
                params: [
                    StatParams.MATCHES, StatParams.CLEAN_SHEETS, StatParams.PENALTIES_SAVED, StatParams.YELLOW_CARDS, StatParams.RED_CARDS
                ],
                default: StatParams.CLEAN_SHEETS
            }
        } else {
            filters = {
                params: [
                    StatParams.MATCHES, StatParams.GOALS, StatParams.ASSISTS, StatParams.YELLOW_CARDS, StatParams.RED_CARDS
                ],
                default: StatParams.GOALS
            }
        }

        filters.params = filters.params.filter((param) => playerStats[param] > 0);

        /*if (teamContext !== TeamContext.ACADEMY && teamContext !== TeamContext.RODINA_M) {
            filters.params = [];
        }*/
    }

    const selector = (
        <PlayerTournamentSelectorWidget
            playerId={playerId}
            teamContext={teamContext}
            onTournamentChange={(tournamentUuid, seasonUuid) => {
                setTournamentUuids({ tournamentUuid, seasonUuid });
            }}
        />
    );

    return (
        <>
            <div className="hidden sm:flex pb-4 items-center justify-center">
                {selector}
            </div>
            <div className="flex flex-row justify-center items-center gap-10 p-3 sm:p-0 w-full">
                <StatsContainer className="!hidden sm:!flex">
                    <StatParameter name="Матчей" value={playerStats?.matches || 0} />
                    {!isGk && <StatParameter name="Голов" value={playerStats?.goals || 0} />}
                    {!isGk && <StatParameter name="Ассистов" value={playerStats?.assists || 0} />}
                    {isGk && <StatParameter name="Сухих" value={playerStats?.cleanSheets || 0} />}
                    {isGk && <StatParameter name="Отбитые пенальти" value={playerStats?.penaltiesSaved || 0} />}
                </StatsContainer>
                <InfoContainer className="rounded-2xl">
                    <div className="flex flex-col gap-6 sm:gap-8 items-center justify-center p-2">
                        {playerData.birthDate && (
                            <InfoParameter
                                name="Дата рождения"
                                value={getFormattedDate(new Date(playerData.birthDate))}
                            />
                        )}
                        {playerData.birthPlace && <InfoParameter name="Место рождения" value={playerData.birthPlace} />}
                        {!playerData.nextClub && playerData.prevClub && <InfoParameter name="Пришёл из" value={playerData.prevClub} />}
                        {playerData.nextClub && <InfoParameter name="Ушёл в" value={playerData.nextClub} />}
                        {debutMatch}
                    </div>
                </InfoContainer>
                <StatsContainer className="!hidden sm:!flex">
                    <StatParameter name="Минут" value={playerStats?.minutesPlayed || 0} />
                    <StatParameter name="Предупреждений" value={playerStats?.yellowCards || 0} nameSize={18} />
                    <StatParameter name="Удалений" value={playerStats?.redCards || 0} />
                </StatsContainer>
            </div>
            <div className="sm:hidden w-full">
                <div className="pb-8 flex items-center justify-center">
                    {selector}
                </div>
                <div className="flex flex-row flex-wrap mx-auto gap-3 items-center justify-center">
                    <StatParameter name="Матчей" value={playerStats?.matches || 0} nameSize={20} />
                    {!isGk && <StatParameter name="Голов" value={playerStats?.goals || 0} nameSize={20} />}
                    {!isGk && <StatParameter name="Ассистов" value={playerStats?.assists || 0} nameSize={20} />}
                    {isGk && <StatParameter name="Сухих" value={playerStats?.cleanSheets || 0} nameSize={20} />}
                    {isGk && <StatParameter name="Отбитые пенальти" value={playerStats?.penaltiesSaved || 0} nameSize={20} />}
                    <StatParameter name="Минут" value={playerStats?.minutesPlayed || 0} nameSize={20} />
                    <StatParameter name="Предупреждений" value={playerStats?.yellowCards || 0} nameSize={14} />
                    <StatParameter name="Удалений" value={playerStats?.redCards || 0} nameSize={20} />
                </div>
            </div>
            {playerStats && <PlayerMatchesWidget
                playerId={playerId}
                playerData={playerData}
                teamContext={teamContext}
                tournamentUuid={tournamentUuids.tournamentUuid}
                seasonUuid={tournamentUuids.seasonUuid}
                teamId={teamId}
                filters={filters?.params}
                defaultFilter={filters?.default}
            />}
        </>
    );
};
